import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Tabs, Tab, Chip, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useRecoilState } from "recoil";
import LoadingSpinner from "../common/loading";
import { settingBackPathState, userInfoState } from "../global-state";
import {
  getIntegration,
  installIntegration,
  syncEntities,
} from "./integration-service";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import { useHistory, useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncIcon from "@mui/icons-material/Sync";
import EntityListView from "./entity-list-view";
import GroupListView from "./group-list-view";
import ProviderListView from "./provider-list-view";
import InfoContentView from "./info-content-view";

const IntegrationDetailView = ({ showFilter = false, setShowFilter }) => {
  const { apiCall } = useApiCall();
  const theme = useTheme();
  const { successToast, errorToast } = useToast();
  const [userInfo] = useRecoilState(userInfoState);
  const [integrationData, setIntegrationData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [settingBackPath, setSettingBackPath] =
    useRecoilState(settingBackPathState);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const integrationId = searchParams.get("id");

  const handleLoadIntegration = async () => {
    setLoading(true);
    try {
      const resp = await getIntegration(
        apiCall,
        userInfo.activeScope.id,
        integrationId
      );
      if (resp) {
        setIntegrationData(resp);
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleLoadIntegration();
  }, [integrationId]);

  const handleGroupChanged = (groups) => {
    setIntegrationData((prev) => ({ ...prev, groups }));
  };

  const getAvailableTabs = () => {
    if (!integrationData) return [];

    const tabs = [];

    // Check each type in meta.entityEnabled
    if (integrationData.meta?.entityEnabled) {
      Object.entries(integrationData.meta.entityEnabled).forEach(
        ([type, enabled]) => {
          if (enabled) {
            const typeName = integrationData.meta.typeNames[type];
            if (typeName) {
              tabs.push({
                hash: "entity",
                label: typeName,
                content: (
                  <EntityListView
                    meta={integrationData.meta}
                    entities={integrationData.entities[type] || []}
                    showFilter={showFilter}
                  />
                ),
              });
            }
          }
        }
      );
    }

    // Check each type in meta.groupEnabled
    if (integrationData.meta?.groupEnabled) {
      Object.entries(integrationData.meta.groupEnabled).forEach(
        ([type, enabled]) => {
          if (enabled) {
            const typeName = integrationData.meta.typeNames[type];
            if (typeName) {
              tabs.push({
                hash: "group",
                label: `${typeName} Group`,
                content: (
                  <GroupListView
                    onGroupChanged={handleGroupChanged}
                    groupData={integrationData.groups[type] || []}
                    entityData={integrationData.entities[type] || []}
                    entityType={type}
                    showFilter={showFilter}
                  />
                ),
              });
            }
          }
        }
      );
    }

    if (integrationData?.meta.providerEnabled) {
      tabs.push({
        hash: "provider",
        label: "Provider",
        content: (
          <ProviderListView
            providers={integrationData.providers}
            showFilter={showFilter}
            onLoadIntegration={handleLoadIntegration}
          />
        ),
      });
    }

    tabs.push({
      hash: "info",
      label: "Info",
      content: (
        <InfoContentView
          integrationData={integrationData}
          location={location}
          setSettingBackPath={setSettingBackPath}
          history={history}
        />
      ),
    });

    return tabs;
  };

  const goTab = (newHash) => {
    const searchParams = new URLSearchParams(location.search);
    const searchString = searchParams.toString();
    let newUrl = location.pathname;
    if (searchString) {
      newUrl += `?${searchString}`;
    }
    if (newHash) {
      newUrl += `#${newHash}`;
    }
    history.replace(newUrl);
  };

  const handleTabChange = (event, newValue) => {
    const tabs = getAvailableTabs();
    const newHash = tabs[newValue].hash || "";
    goTab(newHash);
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (!integrationData) return;

    const tabs = getAvailableTabs();
    if (!integrationData.installed) {
      goTab("info");
    }

    const hash = location.hash.replace("#", "");
    let newIndex = tabs.findIndex((tab) => tab.hash === hash);
    if (newIndex === -1) newIndex = 0;
    setTabIndex(newIndex);
  }, [integrationData]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    let newIndex = tabs.findIndex((tab) => tab.hash === hash);
    if (newIndex === -1) newIndex = 0;
    setTabIndex(newIndex);
  }, [location.hash]);

  const handleInstall = async (integration) => {
    const resp = await installIntegration(
      apiCall,
      userInfo.activeScope.id,
      integration
    );
    if (resp) {
      successToast("Installed");
      handleLoadIntegration();
    } else {
      errorToast("Error!");
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    const response = await syncEntities(
      apiCall,
      userInfo.activeScope.id,
      integrationId
    );
    if (response) {
      successToast("Sync successful");
      handleLoadIntegration(); // Reload integration data after sync
    } else {
      errorToast("Error!");
    }
    setIsSyncing(false);
  };

  const tabs = getAvailableTabs();

  return (
    <Box sx={{ padding: 2 }}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        integrationData && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4">{integrationData.name}</Typography>
              {integrationData.installed ? (
                <Chip
                  label="Installed"
                  color="success"
                  icon={<CheckCircleOutlineIcon />}
                  sx={{
                    backgroundColor: theme.palette.background.l2,
                    color: "white",
                  }}
                />
              ) : (
                <Chip
                  label="Not Installed"
                  color="error"
                  sx={{
                    backgroundColor: theme.palette.background.l4,
                    color: "white",
                  }}
                />
              )}
              {integrationData.installed && integrationData.entityEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}
                  disabled={isSyncing}
                  sx={{
                    height: 32,
                    textTransform: "none",
                    borderRadius: 16,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 0.5,
                    },
                  }}
                >
                  Sync
                </Button>
              )}
              {!integrationData.providerEnabled &&
                !integrationData.installed &&
                integrationData.integration !== "basic.time" && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleInstall(integrationData.integration)}
                  >
                    Install
                  </Button>
                )}
            </Box>
            <Typography variant="subtitle1" color="textSecondary">
              {integrationData.org}
            </Typography>

            <Box
              sx={{
                mt: 4,
                pb: "64px",
                overflow: "auto",
              }}
            >
              {tabs[tabIndex]?.content}
            </Box>

            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: theme.palette.background.paper,
                borderTop: 1,
                borderColor: "divider",
                zIndex: 1100,
              }}
            >
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  "& .MuiTabs-scroller": {
                    display: "flex",
                    justifyContent: "center",
                  },
                  "& .MuiTabs-flexContainer": {
                    justifyContent: "center",
                  },
                  "& .MuiTabs-scrollButtons": {
                    width: 28,
                    backgroundColor: theme.palette.background.paper,
                  },
                  "& .Mui-selected": {
                    fontWeight: "bold",
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    sx={{
                      minWidth: 100,
                      minHeight: 48,
                      textTransform: "none",
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </>
        )
      )}
    </Box>
  );
};

export default IntegrationDetailView;
